import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { LifeBuoy, Send, Clock, CheckCircle, CircleDot, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";

const CATEGORIES = [
  { value: "billing",   label: "Facturation & paiements" },
  { value: "technical", label: "Problème technique" },
  { value: "account",   label: "Mon compte" },
  { value: "feature",   label: "Suggestion de fonctionnalité" },
  { value: "other",     label: "Autre" },
];

const STATUS_CONFIG = {
  open:        { label: "Ouvert",      icon: CircleDot,    className: "bg-blue-50 text-blue-600 border-blue-200" },
  in_progress: { label: "En cours",   icon: Clock,        className: "bg-orange-50 text-orange-600 border-orange-200" },
  resolved:    { label: "Résolu",     icon: CheckCircle,  className: "bg-green-50 text-green-600 border-green-200" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.open;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.className}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function TicketRow({ ticket }) {
  const [open, setOpen] = useState(false);
  const cat = CATEGORIES.find(c => c.value === ticket.category)?.label ?? ticket.category;

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900 truncate">{ticket.subject}</span>
            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full flex-shrink-0">{cat}</span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            {new Date(ticket.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <StatusBadge status={ticket.status} />
        {open ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0">
          <div className="border-t border-slate-100 pt-3 space-y-3">
            {/* Message utilisateur */}
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-slate-600">Moi</span>
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{ticket.message}</p>
              </div>
            </div>
            {/* Réponse admin */}
            {ticket.admin_reply && (
              <div className="flex gap-3 ml-4">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <div className="flex-1 bg-blue-50 border border-blue-100 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-blue-700">Équipe Support</span>
                    {ticket.replied_at && (
                      <span className="text-xs text-blue-400">
                        {new Date(ticket.replied_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.admin_reply}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ category: "", subject: "", message: "" });

  useEffect(() => {
    api.get("/support")
      .then(r => setTickets(r.data))
      .catch(() => {})
      .finally(() => setLoadingTickets(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.subject.trim() || !form.message.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support", form);
      toast.success("Votre demande a bien été envoyée !");
      setTickets(prev => [data, ...prev]);
      setForm({ category: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <LifeBuoy className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Support</h1>
          <p className="text-slate-400 mt-1 text-sm">Une question ? Un problème ? Notre équipe vous répond.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 mt-8">
        {/* Form */}
        <div className="lg:col-span-3">
          <Card className="border-slate-100">
            <CardContent className="p-6">
              <h2 className="font-heading text-lg font-semibold text-slate-900 uppercase tracking-tight mb-5">Nouvelle demande</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Sender info (readonly) */}
                <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {user?.name?.[0]?.toUpperCase() ?? "?"}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-400">{user?.email}</p>
                  </div>
                </div>

                {/* Category */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Catégorie</label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger className="h-11 bg-slate-50 border-slate-200" data-testid="support-category">
                      <SelectValue placeholder="Sélectionner une catégorie..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Subject */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sujet</label>
                  <Input
                    value={form.subject}
                    onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="Décrivez brièvement votre demande..."
                    maxLength={200}
                    className="h-11 bg-slate-50 border-slate-200"
                    data-testid="support-subject"
                  />
                </div>

                {/* Message */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Message</label>
                  <Textarea
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Décrivez votre problème en détail..."
                    rows={5}
                    className="bg-slate-50 border-slate-200 resize-none"
                    data-testid="support-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !form.category || !form.subject.trim() || !form.message.trim()}
                  className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/20"
                  data-testid="support-submit"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Envoyer la demande
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar info */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-slate-100 bg-slate-50/50">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-heading text-sm font-semibold text-slate-700 uppercase tracking-wider">Comment ça marche ?</h3>
              <div className="space-y-3">
                {[
                  { step: "1", text: "Soumettez votre demande via le formulaire" },
                  { step: "2", text: "Notre équipe examine votre ticket" },
                  { step: "3", text: "Vous recevez une réponse par email" },
                ].map(({ step, text }) => (
                  <div key={step} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {step}
                    </div>
                    <p className="text-sm text-slate-600">{text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100 bg-slate-50/50">
            <CardContent className="p-5">
              <h3 className="font-heading text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Délai de réponse</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Problème technique</span>
                  <span className="font-semibold text-slate-700">24h</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Facturation</span>
                  <span className="font-semibold text-slate-700">48h</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Autre</span>
                  <span className="font-semibold text-slate-700">72h</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Ticket history */}
      <div className="mt-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900 uppercase tracking-tight mb-4">Mes demandes</h2>
        {loadingTickets ? (
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="p-4 border border-slate-100 rounded-xl flex items-center gap-3">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-28" />
                </div>
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
            ))}
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-slate-200 rounded-2xl">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <LifeBuoy className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-sm text-slate-400">Aucune demande envoyée pour le moment</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map(t => <TicketRow key={t.ticket_id} ticket={t} />)}
          </div>
        )}
      </div>
    </div>
  );
}
